import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import LandingPageTbond from './components/transferbond/landingPageTbond';
import RequestTransferBond from './components/transferbond/requestLandingpage';
import PostingLandingPage from './components/transferbond/postingLandingpage';
import HubLandingPage from './components/shared/HubLandingPage';
import LargeBondLandingPage from './components/largebond/LargeBondLandingPage';
import CreateLargeBond from './components/largebond/CreateLargeBond';
import ErrorPage from './components/error/error';
import TBondLayout from './components/shared/transferlayout';
import HubLayout from './components/shared/HubLayout';
import theme from './theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import { UserProvider } from './services/userContext';
import { NotificationProvider } from './services/notificationContext';
import SessionExpired from './components/error/sessionExpired';
import ViewDetails from './components/transferbond/requestingSubComponents/requestingViewDetails';
import PostingViewDetails from './components/transferbond/postingsubcomponents/postingViewDetails';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './theme/customToast.css';

// New Oath Auth imports
import { OathAuthProvider } from './services/oathAuthContext';
import { OathSettingsProvider } from './services/oathSettingsContext';
import { OathNotificationProvider } from './services/oathNotificationContext';
import LoginPage from './components/auth/LoginPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import WelcomePage from './components/auth/WelcomePage';
import RegisterPage from './components/auth/RegisterPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MobileLayout from './components/shared/MobileLayout';
import Dashboard from './components/app/Dashboard';
import PlaceholderPage from './components/app/PlaceholderPage';
import AdminPage from './components/app/admin/AdminPage';
import EditUserPage from './components/app/admin/EditUserPage';
import AddUserPage from './components/app/admin/AddUserPage';
import TransferBondsPage from './components/app/transferbonds/TransferBondsPage';
import LargeBondsPage from './components/app/largebonds/LargeBondsPage';
import RequestDetailPage from './components/app/transferbonds/RequestDetailPage';
import PostingDetailPage from './components/app/transferbonds/PostingDetailPage';
import ProfilePage from './components/app/profile/ProfilePage';
import SettingsPage from './components/app/settings/SettingsPage';
import VitalsDrillDownPage from './components/app/VitalsDrillDownPage';
import { PowerReportingPage, PowerReportDetailPage as NewPowerReportDetailPage, PowerLogPage, PowerReceiptsPage } from './components/app/powerreporting';
import { PowerInquiryPage, DocumentViewerPage } from './components/app/powerinquiry';
import { FormsPage } from './components/app/forms';
import { PowerInventoryPage } from './components/app/powerinventory';

import { DischargePage } from './components/app/discharges';
import { BsmsReportsPage } from './components/app/reports';
import SubAgencyARPage from './components/app/subagencyar/SubAgencyARPage';
import PaymentPlansPage from './components/app/paymentplans/PaymentPlansPage';
import { ResourceCenterPage } from './components/app/resources';


// Legacy routes content - wrapped in existing providers
const LegacyContent = () => (
  <Routes>
    <Route
      path="/legacy"
      element={
        <TBondLayout>
          <LandingPageTbond />
        </TBondLayout>
      }
    />
    <Route
      path="/hub"
      element={
        <HubLayout>
          <HubLandingPage />
        </HubLayout>
      }
    />
    <Route
      path="/LargeBond"
      element={
        <HubLayout>
          <LargeBondLandingPage />
        </HubLayout>
      }
    />
    <Route
      path="/largebond/create"
      element={
        <HubLayout>
          <CreateLargeBond />
        </HubLayout>
      }
    />
    <Route path="/edit-large-bond" element={<HubLayout>
          <CreateLargeBond />
        </HubLayout>} />

    <Route
      path="/TransferBond"
      element={
        <TBondLayout>
          <LandingPageTbond />
        </TBondLayout>
      }
    />
    <Route
      path="/request-transfer-bond-dashboard"
      element={
        <TBondLayout>
          <RequestTransferBond />
        </TBondLayout>
      }
    />
    <Route
      path="/post-transfer-bond-dashboard"
      element={
        <TBondLayout>
          <PostingLandingPage />
        </TBondLayout>
      }
    />
    <Route
      path="/view-details"
      element={
        <TBondLayout>
          <ViewDetails />
        </TBondLayout>
      }
    />
    <Route
      path="/view-quote-details"
      element={
        <TBondLayout>
          <PostingViewDetails />
        </TBondLayout>
      }
    />
    <Route path="/error" element={<ErrorPage />} />
    <Route path="/session-expired" element={<SessionExpired />} />
  </Routes>
);

// Legacy routes wrapper with providers
const LegacyRoutes = () => (
  <UserProvider>
    <NotificationProvider>
      <LegacyContent />
    </NotificationProvider>
  </UserProvider>
);

// New mobile app routes - nested under MobileLayout
const NewAppRoutes = () => (
  <Routes>
    <Route
      element={
        <ProtectedRoute requiredPermissions={['READ:PORTAL']}>
          <MobileLayout />
        </ProtectedRoute>
      }
    >
      {/* Dashboard - default landing */}
      <Route index element={<Navigate to="/app/dashboard" replace />} />
      <Route path="dashboard" element={<Dashboard />} />

      {/* Forms */}
      <Route path="forms" element={<ProtectedRoute requiredPermissions={['READ:FORMS']}><FormsPage /></ProtectedRoute>} />

      {/* Transfer Bonds */}
      <Route path="transfer-bonds" element={<ProtectedRoute requiredPermissions={['CREATE:TRANSFER-BOND:REQUEST', 'UPDATE:TRANSFER-BOND:POST']}><TransferBondsPage /></ProtectedRoute>} />
      <Route path="transfer-bonds/request/:id" element={<ProtectedRoute requiredPermissions={['CREATE:TRANSFER-BOND:REQUEST']}><RequestDetailPage /></ProtectedRoute>} />
      <Route path="transfer-bonds/posting/:id" element={<ProtectedRoute requiredPermissions={['UPDATE:TRANSFER-BOND:POST']}><PostingDetailPage /></ProtectedRoute>} />

      {/* Large Bonds */}
      <Route path="large-bonds" element={<ProtectedRoute requiredPermissions={['CREATE:LARGE-BOND:REQUEST']}><LargeBondsPage /></ProtectedRoute>} />

      {/* Power Reports */}
      <Route path="power-reports" element={<ProtectedRoute requiredPermissions={['READ:EXECUTION-REPORT']}><PowerReportingPage /></ProtectedRoute>} />
      <Route path="power-reports/:reportId" element={<ProtectedRoute requiredPermissions={['READ:EXECUTION-REPORT']}><NewPowerReportDetailPage /></ProtectedRoute>} />

      {/* Power Inquiry */}
      <Route path="power-inquiry" element={<ProtectedRoute requiredPermissions={['READ:POWERS']}><PowerInquiryPage /></ProtectedRoute>} />
      <Route path="power-inquiry/documents/:powerNo/:docId" element={<ProtectedRoute requiredPermissions={['READ:POWERS']}><DocumentViewerPage /></ProtectedRoute>} />

      {/* Power Log */}
      <Route path="power-log" element={<ProtectedRoute requiredPermissions={['READ:POWER-LOG']}><PowerLogPage /></ProtectedRoute>} />

      {/* Power Receipts */}
      <Route path="power-receipts" element={<ProtectedRoute><PowerReceiptsPage /></ProtectedRoute>} />

      {/* Power Inventory */}
      <Route path="power-inventory" element={<ProtectedRoute requiredPermissions={['READ:POWER-REASSIGNMENT']}><PowerInventoryPage /></ProtectedRoute>} />

      {/* Discharges */}
      <Route path="discharges" element={<ProtectedRoute requiredPermissions={['UPDATE:POWER:DISCHARGE-REINSTATE']}><DischargePage /></ProtectedRoute>} />

      {/* Reports */}
      <Route path="reports" element={<ProtectedRoute requiredPermissions={['READ:REPORT-CENTER']}><BsmsReportsPage /></ProtectedRoute>} />

      {/* Sub-Agency A/R */}
      <Route path="sub-agency-ar" element={<ProtectedRoute requiredAllPermissions={['READ:SUB-AGENCIES', 'READ:AR']}><SubAgencyARPage /></ProtectedRoute>} />
      <Route path="payment-plans" element={<ProtectedRoute requiredPermissions={['READ:AR']}><PaymentPlansPage /></ProtectedRoute>} />

      {/* Resource Center - available to all portal users */}
      <Route path="resources" element={<ResourceCenterPage />} />

      {/* Profile - no special permission required */}
      <Route path="profile" element={<ProfilePage />} />

      {/* Settings - no special permission required */}
      <Route path="settings" element={<SettingsPage />} />

      {/* Vitals Drill-down */}
      <Route path="vitals/:category/:item" element={<VitalsDrillDownPage />} />

      {/* Admin */}
      <Route path="admin" element={<ProtectedRoute requiredPermissions={['UPDATE:USERS', 'UPDATE:AGENCY:PROFILE', 'READ:USERS', 'UPDATE:AGENCY:PAYMENT-ACCOUNT']}><AdminPage /></ProtectedRoute>} />
      <Route path="admin/users/new" element={<ProtectedRoute requiredPermissions={['CREATE:USERS']}><AddUserPage /></ProtectedRoute>} />
      <Route path="admin/users/:userId" element={<ProtectedRoute requiredPermissions={['UPDATE:USERS', 'READ:USERS']}><EditUserPage /></ProtectedRoute>} />
    </Route>
  </Routes>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <div className="App">
          <Routes>
            {/* New app routes - login at root and /app/* */}
            <Route
              path="/"
              element={
                <OathAuthProvider>
                  <LoginPage />
                </OathAuthProvider>
              }
            />
            <Route
              path="/forgot-password"
              element={<ForgotPasswordPage />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />
            <Route
              path="/set-password"
              element={<ResetPasswordPage />}
            />
            <Route
              path="/welcome"
              element={<WelcomePage />}
            />
            <Route
              path="/register"
              element={<RegisterPage />}
            />
            <Route
              path="/app/*"
              element={
                <OathAuthProvider>
                  <OathSettingsProvider>
                    <OathNotificationProvider>
                      <NewAppRoutes />
                    </OathNotificationProvider>
                  </OathSettingsProvider>
                </OathAuthProvider>
              }
            />

            {/* Legacy routes - all other paths */}
            <Route path="/*" element={<LegacyRoutes />} />
          </Routes>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
            style={{ top: '80px' }}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
